import type { MapVehicle } from '@/types'

/**
 * The plate embedded in a vehicle id.
 *
 * Ids are built as `${fleetCode}-${plate || index}-${direction}`. Collision
 * suffixes append `~N` after the direction without adding another hyphen. The
 * plate is the middle segment; `fleetCode` and `direction` never contain a
 * hyphen, but a plate might, so the middle is everything between the first and
 * last hyphen.
 */
export function plateFromVehicleId(vehicleId: string): string | undefined {
  const first = vehicleId.indexOf('-')
  const last = vehicleId.lastIndexOf('-')
  if (first < 0 || last <= first) return undefined
  return vehicleId.slice(first + 1, last) || undefined
}

/**
 * Resolve a tracked vehicle across refreshes.
 *
 * Exact id first, then plate. The fallback is not defensive padding: the id
 * **embeds the direction**, so a bus that reaches a terminus and turns around
 * gets a brand-new id and would vanish from a pure id lookup — the tracking
 * screen would report the bus as gone at the exact moment it started the return
 * leg the user is probably waiting for.
 *
 * The plate is the physical vehicle, which is what "track this bus" means.
 */
export function findTrackedVehicle(
  vehicles: MapVehicle[],
  vehicleId: string,
): MapVehicle | undefined {
  const exact = vehicles.find((vehicle) => vehicle.id === vehicleId)
  if (exact) return exact

  const plate = plateFromVehicleId(vehicleId)
  if (!plate) return undefined

  return vehicles.find((vehicle) => vehicle.plate === plate)
}
